import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi } from '../services/api';
import { Card, Badge, Spinner } from '../components/UI';
import { 
  Briefcase, UsersThree, Flag, ListChecks, 
  ArrowRight, Clock, CheckCircle, Circle,
  CurrencyDollar, HardHat
} from '@phosphor-icons/react';
import { format } from 'date-fns';

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    try {
      const response = await dashboardApi.getStats();
      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;
  }

  if (error) {
    return <div className="text-center py-8 text-[#DC2626]">{error}</div>;
  }

  const statCards = [
    { 
      label: 'Total Projects', value: stats?.projects?.total || 0, 
      icon: Briefcase, color: '#4F46E5', bg: '#EEF2FF',
      link: '/projects'
    },
    { 
      label: 'Active Projects', value: stats?.projects?.active || 0, 
      icon: Briefcase, color: '#059669', bg: '#ECFDF5',
      link: '/projects'
    },
    { 
      label: 'Teams', value: stats?.teams || 0, 
      icon: UsersThree, color: '#D97706', bg: '#FFFBEB',
      link: '/teams'
    },
    { 
      label: 'Tasks Pending', value: stats?.tasks?.pending || 0, 
      icon: ListChecks, color: '#DC2626', bg: '#FEF2F2',
      link: '/milestones-tasks'
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-2xl font-bold text-[#1A1F36]">Dashboard</h1>
        <p className="text-sm text-[#888]">Welcome to Project ERP Control System</p>
      </div>

      {/* Stats Grid — color-coded */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, idx) => (
          <Link to={stat.link} key={idx}>
            <Card className="p-4 hover:shadow-md transition-all duration-200 group" data-testid={`stat-card-${idx}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-[#888] uppercase tracking-wide">{stat.label}</p>
                  <p className="text-3xl font-bold mt-1" style={{ color: stat.color }}>{stat.value}</p>
                </div>
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform"
                  style={{ backgroundColor: stat.bg }}
                >
                  <stat.icon className="w-5 h-5" style={{ color: stat.color }} weight="bold" />
                </div>
              </div>
              <div className="mt-3 h-1 rounded-full" style={{ backgroundColor: `${stat.color}15` }}>
                <div className="h-full rounded-full w-3/4 transition-all" style={{ backgroundColor: stat.color }} />
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Upcoming Milestones */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-[#1A1F36]">Upcoming Milestones</h2>
            <Link to="/milestones-tasks" className="text-xs font-medium text-[#4F46E5] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="space-y-2.5">
            {stats?.upcoming_milestones?.length > 0 ? (
              stats.upcoming_milestones.map((milestone, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 rounded-lg border border-[#F0F0F0] hover:border-[#E0E0E0] transition-colors">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] flex items-center justify-center">
                      <Flag className="w-4 h-4 text-[#4F46E5]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[#1A1F36]">{milestone.name}</p>
                      <p className="text-[11px] text-[#999]">
                        {format(new Date(milestone.planned_date), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                  <Badge variant={milestone.status === 'COMPLETED' ? 'success' : 'warning'}>
                    {milestone.status}
                  </Badge>
                </div>
              ))
            ) : (
              <p className="text-sm text-[#999] text-center py-6">No upcoming milestones</p>
            )}
          </div>
        </Card>

        {/* Task Status */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-[#1A1F36]">Task Overview</h2>
            <Link to="/milestones-tasks" className="text-xs font-medium text-[#4F46E5] hover:underline flex items-center gap-1">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Pending', value: stats?.tasks?.pending || 0, icon: Circle, color: '#6B7280' },
              { label: 'In Progress', value: stats?.tasks?.in_progress || 0, icon: Clock, color: '#D97706' },
              { label: 'Completed', value: stats?.tasks?.completed || 0, icon: CheckCircle, color: '#059669' },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center space-x-2.5">
                  <item.icon className="w-4 h-4" style={{ color: item.color }} weight="fill" />
                  <span className="text-sm text-[#666]">{item.label}</span>
                </div>
                <span className="text-sm font-bold" style={{ color: item.color }}>{item.value}</span>
              </div>
            ))}

            {/* Progress bar */}
            <div className="mt-2">
              <div className="h-2.5 bg-[#F0F1F3] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#059669] rounded-full transition-all duration-700"
                  style={{ 
                    width: `${stats?.tasks?.total > 0 
                      ? ((stats?.tasks?.completed || 0) / stats.tasks.total * 100) 
                      : 0}%` 
                  }}
                />
              </div>
              <p className="text-[11px] text-[#999] text-center mt-1.5">
                {stats?.tasks?.completed || 0} of {stats?.tasks?.total || 0} tasks completed
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Projects */}
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-[#1A1F36]">Recent Projects</h2>
          <Link to="/projects" className="text-xs font-medium text-[#4F46E5] hover:underline flex items-center gap-1">
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E8E8ED]">
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#999] uppercase tracking-wider">Name</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#999] uppercase tracking-wider">Client</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#999] uppercase tracking-wider">Status</th>
                <th className="px-4 py-2.5 text-left text-[10px] font-bold text-[#999] uppercase tracking-wider">Start Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0F0F0]">
              {stats?.recent_projects?.length > 0 ? (
                stats.recent_projects.map((project, idx) => (
                  <tr key={idx} className="hover:bg-[#F8F9FB] transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-[#1A1F36]">{project.name}</td>
                    <td className="px-4 py-3 text-sm text-[#888]">{project.client_name}</td>
                    <td className="px-4 py-3">
                      <Badge variant={
                        project.status === 'ACTIVE' ? 'success' : 
                        project.status === 'PLANNING' ? 'primary' : 
                        project.status === 'COMPLETED' ? 'success' : 'default'
                      }>
                        {project.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#888]">
                      {project.start_date ? format(new Date(project.start_date), 'MMM dd, yyyy') : '-'}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-[#999]">No projects yet</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
